import { useTranslation, Trans } from 'react-i18next';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import ReactGA4 from 'react-ga4';

import { Input, SortOrder, WiderTable } from '@/components';
import { AntdSelect } from '@/components/shared/AntdSelect';
import { useQueryParams, useUserType } from '@/hooks';
import { CHAINS_LIST } from '@/utils';

import { getColumns } from './columns';
import style from './index.module.scss';
import { useCollectionList } from '../../hooks';

const FILTER_CHAIN_KEYS = ['eth', 'matic', 'bsc', 'arb', 'op', 'avax'];

const ENABLE_FILTER_CHAIN_KEYS = ['eth'];

const CHAIN_OPTIONS = CHAINS_LIST.filter(x => FILTER_CHAIN_KEYS.indexOf(x.serverId) !== -1).map(
  chain => ({
    label: chain.name,
    mode: chain.serverId,
    disabledTip: ENABLE_FILTER_CHAIN_KEYS.includes(chain.serverId) ? undefined : (
      <Trans i18nKey="CollectionList.unavailable"></Trans>
    ),
  }),
);

const PAGE_SIZE = 20;

export const CollectionList = () => {
  const { t } = useTranslation();

  const history = useHistory();
  const [selectChain, setSelectChain] = useQueryParams<string | undefined>(
    'chain',
    FILTER_CHAIN_KEYS[0],
  );
  const [searchText, setSearchText] = useQueryParams<string | undefined>('q');
  const [orderBy, setOrderBy] = useQueryParams<string | undefined>('order');
  const [page, setPage] = useQueryParams<number | undefined>('page', 0);
  const userType = useUserType();

  const columns = useMemo(() => getColumns(), []);
  const { data, refetch, isError, isLoading } = useCollectionList(
    selectChain || FILTER_CHAIN_KEYS[0],
  );
  useEffect(() => {
    if (!ENABLE_FILTER_CHAIN_KEYS.includes(selectChain?.trim() ?? ''))
      setSelectChain(ENABLE_FILTER_CHAIN_KEYS[0]);
  }, [selectChain, setSelectChain]);

  useEffect(() => {
    refetch();
  }, [selectChain, refetch]);

  useEffect(() => {
    if (userType) {
      // new
      ReactGA4.event({
        category: 'View NFTs',
        action: `NFTs_List_${userType}`,
      });
    }
  }, [userType]);

  const onRowClick = useCallback(
    row => {
      history.push(`/nfts/${row.chain_id}/${row.id}`);
      if (userType) {
        // new
        ReactGA4.event({
          category: 'View NFTs',
          action: `Click_NFTs_${userType}`,
        });
      }
    },
    [history, userType],
  );

  const onSort = useCallback(
    ([[key, sort]]: Array<[string, SortOrder]>) => {
      setOrderBy(key ? `${sort === 'desc' ? '-' : ''}${key}` : undefined);
    },
    [setOrderBy],
  );

  const list = useMemo(() => {
    const _search = searchText?.trim().toLocaleLowerCase() || '';

    return _search
      ? data?.collections?.filter(x => x.name.toLowerCase().includes(_search))
      : data?.collections;
  }, [data, searchText]);

  return (
    <>
      <div className={style.listHeader}>
        <div className={style.title}>NFTs</div>
        <div className={style.desc}>{t('CollectionList.ikwkbq')}</div>
      </div>
      <div className={style.collectionList}>
        <div className={style.operationHeader}>
          <span className={style.title}>{t('CollectionList.debdnf')}</span>
          <AntdSelect
            className={style.select}
            dropdownClassName={style.selectDropdown}
            width={120}
            height={35}
            select={selectChain}
            setSelect={setSelectChain}
            selectOptions={CHAIN_OPTIONS}
            forceMulti
          />
          <Input
            className={style.searchInput}
            placeholder={t('CollectionList.collectPlaceholder')}
            onChange={setSearchText}
            value={searchText}
            allowClear
          />
        </div>
        <WiderTable
          className={style.table}
          onRowClick={onRowClick}
          columns={columns}
          data={list}
          reload={refetch}
          isError={isError}
          loading={isLoading}
          pageSize={PAGE_SIZE}
          orderBy={orderBy}
          onSort={onSort}
          pagination={{
            defaultCurrent: page,
            onChange: setPage,
          }}
        />
      </div>
    </>
  );
};
